/**
 * Maps a case's workflow_type to the workflow type the disposition engine
 * (`@100mslive/disposition-engine`) understands. Every type passes through
 * unchanged except the aliases in {@link ENGINE_WORKFLOW_TYPE_ALIASES}.
 *
 * `bho` (BV-2171) was briefly aliased to `unifiedmedbv` while the engine lacked a
 * native handler. The engine now registers `bho` natively (its own 1_009_xxx
 * catalog, incl. "No BHO Coverage"), so passing it through unchanged is correct —
 * aliasing would have borrowed unifiedmedbv's catalog and shown the wrong
 * dispositions. `pa_status_pbm` / `pa_status_medbv` had the same alias dropped
 * for the same reason (BI-1447 / AA-964).
 *
 * This function remains the single seam should any future workflow need to borrow
 * another's engine handler.
 *
 * Overloaded so it preserves the caller's type: a `string` in yields a `string`
 * out (callers that already narrowed away `undefined` don't have it reintroduced),
 * while `string | undefined` passes through unchanged.
 */
export function resolveEngineWorkflowType(workflowType: string): string;
export function resolveEngineWorkflowType(workflowType: undefined): undefined;
export function resolveEngineWorkflowType(workflowType: string | undefined): string | undefined;
export function resolveEngineWorkflowType(workflowType: string | undefined): string | undefined {
  if (workflowType === undefined) return undefined;
  return ENGINE_WORKFLOW_TYPE_ALIASES[workflowType] ?? workflowType;
}

/**
 * Workflows that borrow another workflow's engine handler because the engine has
 * no native one yet. Drop an entry once the engine registers that type natively.
 *
 * - `medbhobv` (BV-2328) uses the MedBV (unifiedmedbv) disposition list — its
 *   1_002_xxx catalog — until the engine ships a medbhobv one.
 */
const ENGINE_WORKFLOW_TYPE_ALIASES: Readonly<Record<string, string>> = {
  medbhobv: 'unifiedmedbv',
};
